import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private baseUrl = environment.apiUrl;
  private realTimeInterval: any;
  
  constructor(private http: HttpClient) {
    this.startRealTimeUpdates();
  }
  
  private startRealTimeUpdates() {
    // Real-time updates every 30 seconds
    this.realTimeInterval = setInterval(() => {
      if (this.isLoggedIn()) {
        this.refreshUserData();
      }
    }, 30000);
    
    // Auto-logout after 30 minutes of inactivity
    this.startInactivityTimer();
  }
  
  private startInactivityTimer() {
    // Backend handles session timeout, just add global error handler
    this.setupGlobalErrorHandler();
  }
  
  private setupGlobalErrorHandler() {
    // Global error handling is done per request via catchError
  }
  
  private refreshUserData() {
    this.getUserLinks(1).subscribe({
      next: (response) => {
        if (response.success && response.data.user) {
          const currentUser = this.getUserData();
          if (currentUser) {
            const updatedUser = { ...currentUser, ...response.data.user };
            localStorage.setItem('userData', JSON.stringify(updatedUser));
            window.dispatchEvent(new CustomEvent('userDataUpdated', { detail: updatedUser }));
          }
        }
      },
      error: (error) => {
        if (error.status === 401 || error.error?.logout) {
          this.logout();
        }
      }
    });
  }
  

  
  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('auth_token');
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    });
  }
  
  // Auth endpoints
  login(userIdOrEmail: string, password: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/login.php`, {
      userIdOrEmail,
      password
    });
  }
  
  register(userId: string, fullName: string, password: string, email?: string, phoneNumber?: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/register.php`, {
      userId,
      fullName,
      password,
      email,
      phoneNumber
    });
  }
  
  // User endpoints
  getUserLinks(limit?: number): Observable<any> {
    const params = limit ? `?limit=${limit}` : '';
    return this.http.get(`${this.baseUrl}/user/links-simple.php${params}`, {
      headers: this.getHeaders()
    }).pipe(
      catchError(error => {
        if (error.status === 401 || error.error?.logout) {
          this.logout();
        }
        return throwError(() => error);
      })
    );
  }
  
  submitLinks(urls: string, dripEnabled: boolean = false, dripInterval: number = 60): Observable<any> {
    return this.http.post(`${this.baseUrl}/user/submit-links.php`, {
      urls,
      drip_enabled: dripEnabled,
      drip_interval: dripInterval
    }, {
      headers: this.getHeaders()
    }).pipe(
      catchError(error => {
        if (error.status === 401 || error.error?.logout) {
          this.logout();
        }
        return throwError(() => error);
      })
    );
  }
  
  getDripSettings(): Observable<any> {
    return this.http.get(`${this.baseUrl}/user/drip-status-simple-auth.php`, {
      headers: this.getHeaders()
    });
  }
  
  updateDripSettings(dripEnabled: boolean, dripInterval: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/user/drip-settings.php`, {
      drip_enabled: dripEnabled,
      drip_interval: dripInterval
    }, {
      headers: this.getHeaders()
    });
  }
  
  // Utility methods
  isLoggedIn(): boolean {
    return !!localStorage.getItem('auth_token');
  }
  
  logout(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('userData');
    window.location.href = '/login';
  }
  
  getUserData(): any {
    try {
      const userData = localStorage.getItem('userData');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.warn('Failed to parse user data from localStorage');
      return null;
    }
  }
  
  stopRealTimeUpdates() {
    if (this.realTimeInterval) {
      clearInterval(this.realTimeInterval);
    }
  }
}
